import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { AccessRuleType, type CustomerAccessRuleToken } from '@sleed/bizcommerce-api';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface GroupOption {
	id?: number | null;
	nm?: string | null;
}

interface Props {
	rows: CustomerAccessRuleToken[];
	groups: GroupOption[];
	isLoading: boolean;
	/** i18n key prefix exposing `.add`, `.group`, `.selectGroup` (+ `.ruleType`, `.allow`, `.deny` when withRuleType). */
	labelPrefix: string;
	/** Show the Allow/Deny radio (for new rows), a RuleType column, and the inline switch. */
	withRuleType?: boolean;
	emptyIcon: ReactNode;
	isAdding?: boolean;
	onAdd: (groupId: number, ruleType: AccessRuleType) => void;
	onRemove: (id: number) => void;
	/** Change an existing row's RuleType in place (only meaningful when withRuleType). */
	onSetRuleType?: (row: CustomerAccessRuleToken, ruleType: AccessRuleType) => void;
}

/**
 * Shared editor for a customer-group → CustomerAccessRule list: a group picker + Add, and a table of assigned
 * groups with delete. With `withRuleType`, it also exposes an Allow/Deny radio for new rows and an inline
 * Allow/Deny switch per existing row. Used by the per-customer access-rules dialog and the review-exclusions
 * dialog (the latter is Deny-only, so it omits the RuleType affordances).
 */
export function AccessRuleGroupsManager({
	rows,
	groups,
	isLoading,
	labelPrefix,
	withRuleType = false,
	emptyIcon,
	isAdding,
	onAdd,
	onRemove,
	onSetRuleType
}: Props) {
	const { t } = useTranslation();
	const [groupId, setGroupId] = useState('');
	const [ruleType, setRuleType] = useState<AccessRuleType>(AccessRuleType.Allow);

	const assignedIds = new Set(rows.map(r => r.CustomerGroupId));
	const available = groups.filter(g => g.id != null && !assignedIds.has(g.id));
	const groupName = (id: number) => groups.find(g => g.id === id)?.nm ?? `#${id}`;

	const add = () => {
		if (!groupId) return;
		onAdd(Number(groupId), ruleType);
		setGroupId('');
		setRuleType(AccessRuleType.Allow);
	};

	return (
		<>
			{withRuleType && (
				<div className="flex items-center gap-4 text-sm" role="radiogroup" aria-labelledby="car-rule-type-label">
					<span id="car-rule-type-label" className="text-foreground-muted">
						{t(`${labelPrefix}.ruleType`)}
					</span>
					<label className="flex cursor-pointer items-center gap-1.5">
						<input
							type="radio"
							name="car-rule-type"
							checked={ruleType === AccessRuleType.Allow}
							onChange={() => setRuleType(AccessRuleType.Allow)}
						/>
						{t(`${labelPrefix}.allow`)}
					</label>
					<label className="flex cursor-pointer items-center gap-1.5">
						<input
							type="radio"
							name="car-rule-type"
							checked={ruleType === AccessRuleType.Deny}
							onChange={() => setRuleType(AccessRuleType.Deny)}
						/>
						{t(`${labelPrefix}.deny`)}
					</label>
				</div>
			)}

			<div className="flex items-end gap-2">
				<div className="flex-1">
					<Select value={groupId} onValueChange={setGroupId}>
						<SelectTrigger className="w-full" aria-label={t(`${labelPrefix}.group`)}>
							<SelectValue placeholder={t(`${labelPrefix}.selectGroup`)} />
						</SelectTrigger>
						<SelectContent>
							{available.map(g => (
								<SelectItem key={g.id} value={String(g.id)}>
									{g.nm}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<Button onClick={add} disabled={!groupId || isAdding}>
					<Plus size={15} /> {t(`${labelPrefix}.add`)}
				</Button>
			</div>

			<div className="max-h-[50vh] overflow-auto rounded-md border border-border">
				{isLoading ? (
					<div className="flex flex-col gap-2 p-3">
						{Array.from({ length: 3 }).map((_, i) => (
							<Skeleton key={i} className="h-9 w-full rounded-md" />
						))}
					</div>
				) : rows.length === 0 ? (
					<div className="flex flex-col items-center gap-3 py-12 text-center">
						{emptyIcon}
						<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
					</div>
				) : (
					<table className="w-full border-collapse text-sm">
						<tbody>
							{rows.map(r => (
								<tr key={r.id} className="border-b border-border/60 hover:bg-muted/50">
									<td className="px-3 py-2 text-foreground">{r.customerGroupName || groupName(r.CustomerGroupId)}</td>
									{withRuleType && (
										<td className="px-3 py-2">
											<div className="inline-flex overflow-hidden rounded-md border border-border">
												{[AccessRuleType.Allow, AccessRuleType.Deny].map(rt => (
													<button
														key={rt}
														type="button"
														aria-pressed={r.RuleType === rt}
														onClick={() => r.RuleType !== rt && r.id != null && onSetRuleType?.(r, rt)}
														className={cn(
															'px-2 py-0.5 text-xs transition-colors',
															r.RuleType === rt ? 'bg-primary text-primary-fg' : 'text-foreground-muted hover:bg-muted'
														)}>
														{rt === AccessRuleType.Deny ? t(`${labelPrefix}.deny`) : t(`${labelPrefix}.allow`)}
													</button>
												))}
											</div>
										</td>
									)}
									<td className="px-2 py-1 text-right" style={{ width: 48 }}>
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7 text-danger-fg"
											aria-label={t('common.delete')}
											onClick={() => r.id != null && onRemove(r.id)}>
											<Trash2 size={14} />
										</Button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>
		</>
	);
}
